import PropTypes from 'prop-types';
import { createContext, useState, useEffect, useCallback } from 'react';
import { withApi } from 'wrappers';
import endPoints from 'endPoints/endPoints';

const ThemeContext = createContext({
  themes: [],
  activeTheme: null,
  loading: false,
  error: null,
  loadThemes: () => {},
  loadActiveTheme: () => {},
  setActiveTheme: async () => {},
  createTheme: async () => {},
  updateTheme: async () => {},
  deleteTheme: async () => {},
});

function ThemeProviderComponent({ children, doGet, doPost, doPut, doDelete }) {
  const [themes, setThemes] = useState([]);
  const [activeTheme, setActiveThemeState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadThemes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await doGet({ url: endPoints.themes.base });
      if (response.success && response.data) {
        setThemes(response.data);
      }
    } catch (err) {
      setError(err.message || 'Error al cargar temas');
      console.error('Error loading themes:', err);
    } finally {
      setLoading(false);
    }
  }, [doGet]);

  const loadActiveTheme = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await doGet({ url: endPoints.themes.active });
      if (response.success && response.data) {
        setActiveThemeState(response.data);
        return response.data;
      }
    } catch (err) {
      setError(err.message || 'Error al cargar tema activo');
      console.error('Error loading active theme:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [doGet]);

  const setActiveTheme = useCallback(async (themeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await doPost({
        url: endPoints.themes.setActive,
        data: { theme_id: themeId },
      });
      if (response.success) {
        // Reload the active theme to get the full theme data with config
        const activeThemeData = await loadActiveTheme();
        if (activeThemeData) {
          setActiveThemeState(activeThemeData);
        } else {
          // If loadActiveTheme doesn't return data, use the theme from the list
          const themeFromList = themes.find(t => t.id === themeId);
          if (themeFromList) {
            setActiveThemeState(themeFromList);
          }
        }
        return activeThemeData || response.data;
      }
    } catch (err) {
      setError(err.message || 'Error al establecer tema activo');
      console.error('Error setting active theme:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [doPost, loadActiveTheme, themes]);

  const createTheme = useCallback(async (themeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await doPost({
        url: endPoints.themes.base,
        data: themeData,
      });
      if (response.success && response.data) {
        await loadThemes();
        return response.data;
      }
    } catch (err) {
      setError(err.message || 'Error al crear tema');
      console.error('Error creating theme:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [doPost, loadThemes]);

  const updateTheme = useCallback(async (themeId, themeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await doPut({
        url: `${endPoints.themes.base}/${themeId}`,
        data: themeData,
      });
      if (response.success && response.data) {
        // Update themes list
        setThemes((prev) => prev.map((t) => (t.id === themeId ? response.data : t)));
        
        // If this is the active theme, reload it to get the latest config
        if (activeTheme && activeTheme.id === themeId) {
          await loadActiveTheme();
        }
        return response.data;
      }
    } catch (err) {
      setError(err.message || 'Error al actualizar tema');
      console.error('Error updating theme:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [doPut, activeTheme, loadActiveTheme]);

  const deleteTheme = useCallback(async (themeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await doDelete({
        url: `${endPoints.themes.base}/${themeId}`,
      });
      if (response.success) {
        await loadThemes();
        if (activeTheme && activeTheme.id === themeId) {
          // Reset to default theme if deleting active theme
          await loadActiveTheme();
        }
      }
    } catch (err) {
      setError(err.message || 'Error al eliminar tema');
      console.error('Error deleting theme:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [doDelete, loadThemes, loadActiveTheme, activeTheme]);

  // Load themes and active theme on mount
  useEffect(() => {
    loadThemes();
    loadActiveTheme();
  }, [loadThemes, loadActiveTheme]);

  return (
    <ThemeContext.Provider
      value={{
        themes,
        activeTheme,
        loading,
        error,
        loadThemes,
        loadActiveTheme,
        setActiveTheme,
        createTheme,
        updateTheme,
        deleteTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

ThemeProviderComponent.propTypes = {
  children: PropTypes.node,
  doGet: PropTypes.func,
  doPost: PropTypes.func,
  doPut: PropTypes.func,
  doDelete: PropTypes.func,
};

const ThemeProvider = withApi(ThemeProviderComponent);

export { ThemeProvider, ThemeContext };

